import { Box } from '@mui/joy';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Total } from 'core/elements';
import { SubmitValidationWarning } from 'quotes/components/CustomerQuotePartList/QuotePartListTable/Actions/SubmitCustomerOrder/SubmitValidationWarning';
import { getFooterValidationWarnings } from 'quotes/components/CustomerQuotePartList/QuotePartListTable/Actions/SubmitCustomerOrder/utils';
import { QuoteEntityType, ValidationType } from 'quotes/enums';
import { QuotePartHelper } from 'quotes/helpers';
import { ICustomerQuote, ICustomerQuotePart, IQuotePart } from 'quotes/types';

import { getOrderWarningTooltipLabel } from './utils';
import { WarningCountLine } from './WarningCountLine';

type IProps = {
  records: IQuotePart[];
  quoteEntityType?: QuoteEntityType;
  classNames: string;
  quote?: ICustomerQuote;
  onUpdateQuote?: (quote: ICustomerQuote) => void;
};

export const Warnings = ({
  records,
  quoteEntityType = QuoteEntityType.QUOTE,
  classNames,
  quote,
  onUpdateQuote,
}: IProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { t } = useTranslation();

  const modalWarnings = useMemo(() => {
    if (!quote || !onUpdateQuote) {
      return null;
    }

    return getFooterValidationWarnings(quote, records as ICustomerQuotePart[], quoteEntityType);
  }, [quote, onUpdateQuote, records, quoteEntityType]);

  const lineErrors = records
    .map((record) =>
      QuotePartHelper.validationErrors({
        data: record,
        statuses: Object.values(ValidationType),
        withValues: false,
        filteredByStatuses: false,
        quoteEntityType,
      })
    )
    .flat()
    .filter((error) => !!error);

  const groupedErrorsCount = lineErrors.reduce(
    (acc, error) => {
      acc[error] = (acc[error] || 0) + 1;

      return acc;
    },
    {} as Record<string, number>
  );

  modalWarnings?.orderWarnings?.forEach((orderWarning) => {
    const label = getOrderWarningTooltipLabel(orderWarning);

    groupedErrorsCount[label] = (groupedErrorsCount[label] || 0) + 1;
  });

  const totalErrorsCount = Object.values(groupedErrorsCount).reduce(
    (acc, cnt): number => acc + cnt,
    0
  );

  const tooltipTitle = (
    <Box>
      {Object.entries(groupedErrorsCount).map(([error, count]) => (
        <WarningCountLine key={error} label={error} count={Number(count)} />
      ))}
    </Box>
  );

  if (!totalErrorsCount) {
    return null;
  }

  return (
    <>
      <Total
        label={t('labelWarnings')}
        value={totalErrorsCount}
        options={{
          classNames,
          warning: tooltipTitle,
          onWarningClick: modalWarnings ? () => setIsModalOpen(true) : undefined,
        }}
      />

      {isModalOpen && modalWarnings && quote && onUpdateQuote && (
        <SubmitValidationWarning
          warnings={modalWarnings}
          quote={quote}
          onUpdateQuote={onUpdateQuote}
          quoteEntityType={quoteEntityType}
          onCancel={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
};
